import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { COMPLAINT_KINDS, complaintsApi, type ComplaintKind } from "../api/complaints";
import { ordersApi, type Order } from "../api/orders";
import { adminApi, type Company } from "../api/admin";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { Select } from "../components/Select";
import { CompanyCombobox } from "../components/CompanyCombobox";
import { LocalAttachmentStager } from "../components/shared/LocalAttachmentStager";
import type { ApiError } from "../api/client";
import { validateNewComplaintForm } from "./complaintsFilter";
import { COMPLAINT_ALLOWED_TYPES, COMPLAINT_MAX_BYTES, COMPLAINT_MAX_FILES } from "../api/complaintUploadRules";

/**
 * New complaint form. Filed by a CUST_PROCUREMENT/CUST_MANAGER user for their
 * own company (no company picker needed — the backend defaults to
 * `caller.companyId`), or by ADMIN/SUPERADMIN filing on a customer's behalf
 * (companyId is required server-side in that case, so the picker only shows
 * for internal callers).
 */
export default function ComplaintNewPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { state } = useAuth();

  const isInternal = state.status === "authenticated" && state.user.internal;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [lot, setLot] = useState("");
  const [orderId, setOrderId] = useState("");
  const [kind, setKind] = useState<ComplaintKind | "">("");
  const [urgent, setUrgent] = useState(false);
  const [claimedQuantity, setClaimedQuantity] = useState("");
  const [claimedUnit, setClaimedUnit] = useState("");
  const [goodsReceivedOn, setGoodsReceivedOn] = useState("");
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [busy, setBusy] = useState(false);

  // Internal on-behalf callers: need the full company list for the picker.
  useEffect(() => {
    if (!isInternal) return;
    void adminApi.listCompanies(0, 200).then((p) => setCompanies(p.content)).catch(() => undefined);
  }, [isInternal]);

  // Order picker source: the customer's own orders, or (internal) the
  // selected on-behalf company's orders once one is chosen.
  useEffect(() => {
    setOrderId("");
    if (isInternal) {
      if (!companyId) { setOrders([]); return; }
      // Padajući spisak porudžbina uz reklamaciju: najnovije, ne cela istorija.
      void ordersApi.list({ companyId, size: 200 })
        .then((r) => setOrders(r.content))
        .catch(() => setOrders([]));
    } else {
      void ordersApi.list({ size: 200 })
        .then((r) => setOrders(r.content))
        .catch(() => setOrders([]));
    }
  }, [isInternal, companyId]);

  async function uploadStaged(complaintId: string) {
    for (const f of files) {
      const presigned = await complaintsApi.presignAttachment(complaintId, {
        fileName: f.name,
        contentType: f.type,
        sizeBytes: f.size,
      });
      const res = await fetch(presigned.url, {
        method: "PUT",
        headers: { "Content-Type": f.type },
        body: f,
      });
      if (!res.ok) throw new Error(`Upload failed for ${f.name}: ${res.status}`);
      await complaintsApi.confirmAttachment(complaintId, {
        objectKey: presigned.objectKey,
        fileName: f.name,
        contentType: f.type,
        sizeBytes: f.size,
      });
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const errors = validateNewComplaintForm({ title });
    if (errors.title) {
      toast.error(t("complaints.new.errTitleRequired", "Title is required"));
      return;
    }
    if (isInternal && !companyId) {
      toast.error(t("complaints.new.errCompanyRequired", "Pick a customer"));
      return;
    }
    setBusy(true);
    try {
      const created = await complaintsApi.create({
        companyId: isInternal ? companyId : undefined,
        title: title.trim(),
        description: description.trim() || null,
        orderId: orderId || null,
        lot: lot.trim() || null,
        kind: kind || null,
        urgent,
        claimedQuantity: claimedQuantity.trim() || null,
        claimedUnit: claimedUnit.trim() || null,
        goodsReceivedOn: goodsReceivedOn || null,
      });
      if (files.length > 0) {
        try {
          await uploadStaged(created.id);
        } catch (_err) {
          toast.error(t("complaints.new.attachFailed",
            "Complaint filed but some attachments failed. Retry from the detail page."));
          navigate(`/complaints/${created.id}`, { replace: true });
          return;
        }
      }
      toast.success(t("complaints.new.toastCreated", "Complaint filed."));
      navigate(`/complaints/${created.id}`, { replace: true });
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ maxWidth: 760, margin: "0 auto" }}>
      <div className="page-head" style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: "var(--ink)" }}>
          {t("complaints.new.title", "New complaint")}
        </h1>
      </div>
      <div className="card">
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {isInternal && (
            <label className="field">
              <span>{t("complaints.new.customer", "Customer")} *</span>
              <CompanyCombobox
                companies={companies}
                value={companyId}
                onChange={setCompanyId}
                placeholder={t("complaints.new.customerPlaceholder", "Filing on behalf of…")}
              />
            </label>
          )}

          <label className="field">
            <span>{t("complaints.new.titleLabel", "Title")} *</span>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
                   required maxLength={300}
                   placeholder={t("complaints.new.titlePlaceholder", "Short summary of the issue")} />
          </label>

          <label className="field">
            <span>{t("complaints.new.description", "Description")}</span>
            <textarea
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }}
              placeholder={t("complaints.new.descriptionPlaceholder", "What happened? When was it noticed?")}
            />
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label className="field">
              <span>{t("complaints.new.order", "Order (optional)")}</span>
              <Select<string>
                value={orderId}
                onChange={setOrderId}
                disabled={isInternal && !companyId}
                ariaLabel={t("complaints.new.order", "Order (optional)")}
                placeholder={t("complaints.new.orderNone", "No order reference")}
                options={[
                  { value: "", label: t("complaints.new.orderNone", "No order reference") },
                  ...orders.map((o) => ({ value: o.id, label: o.orderNumber })),
                ]}
              />
            </label>
            <label className="field">
              <span>{t("complaints.new.lot", "LOT")}</span>
              <input className="input" value={lot} onChange={(e) => setLot(e.target.value)}
                     maxLength={100}
                     placeholder={t("complaints.new.lotPlaceholder", "e.g. L2607-14")} />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label className="field">
              <span>{t("complaints.new.kind", "Vrsta")}</span>
              <Select<string>
                value={kind}
                onChange={(v) => setKind(v as ComplaintKind | "")}
                ariaLabel={t("complaints.new.kind", "Vrsta")}
                options={[
                  { value: "", label: t("complaints.kind.none", "— nije određena —") },
                  ...COMPLAINT_KINDS.map((k) => ({
                    value: k,
                    label: t(`complaints.kind.${k}`, { defaultValue: k }),
                  })),
                ]}
              />
            </label>
            <label className="field" style={{ justifyContent: "flex-end" }}>
              <span>{t("complaints.new.priority", "Prioritet")}</span>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink-2)" }}>
                <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} />
                {t("complaints.new.urgentHint", "Hitno — ide pre ostalih")}
              </label>
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <label className="field">
              <span>{t("complaints.new.claimedQuantity", "Reklamirana količina")}</span>
              <input
                className="input"
                value={claimedQuantity}
                onChange={(e) => setClaimedQuantity(e.target.value)}
                inputMode="decimal"
                placeholder={t("complaints.new.claimedQuantityPlaceholder", "npr. 1200")}
              />
            </label>
            <label className="field">
              <span>{t("complaints.new.claimedUnit", "Jedinica")}</span>
              <input
                className="input"
                value={claimedUnit}
                onChange={(e) => setClaimedUnit(e.target.value)}
                maxLength={20}
                placeholder={t("complaints.new.claimedUnitPlaceholder", "kg, t, vreća")}
              />
            </label>
            <label className="field">
              <span>{t("complaints.new.goodsReceivedOn", "Roba primljena")}</span>
              <input
                className="input"
                type="date"
                value={goodsReceivedOn}
                onChange={(e) => setGoodsReceivedOn(e.target.value)}
              />
            </label>
          </div>

          <div className="field">
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--ink-3)",
                           letterSpacing: ".02em", textTransform: "uppercase" }}>
              {t("complaints.new.attachments", "Attachments")}
            </span>
            <LocalAttachmentStager
              files={files}
              onChange={setFiles}
              disabled={busy}
              allowedTypes={COMPLAINT_ALLOWED_TYPES}
              maxBytes={COMPLAINT_MAX_BYTES}
              maxFiles={COMPLAINT_MAX_FILES}
              i18nPrefix="complaints.attachments."
              fileTypesHint={t("complaints.new.attachmentsHint",
                `Images, PDF, Office docs, ZIP · max 25 MB · up to ${COMPLAINT_MAX_FILES} files`)}
            />
          </div>
        </div>
        <div style={{
          display: "flex", gap: 8, justifyContent: "flex-end",
          padding: "14px 18px", borderTop: "1px solid var(--border)",
          background: "var(--surface-2)", borderRadius: "0 0 var(--r-md) var(--r-md)",
        }}>
          <button type="button" className="btn" onClick={() => navigate(-1)}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? t("complaints.new.filing", "Filing…") : t("complaints.new.submit", "File complaint")}
          </button>
        </div>
      </div>
    </form>
  );
}
